import { useQuery } from '@tanstack/react-query';
import { getTestResults, type ReleaseNote, type TestMark, type TestMarkStatus } from '@/api/releaseApproval';

export const testResultsKey = (sha: string) => ['releases', 'test-results', sha] as const;

/** Testers' marks on this version's changes (#3491); shared by the list and the approval dialog. */
export function useTestResults(sha: string | null) {
  return useQuery({
    queryKey: testResultsKey(sha ?? ''),
    queryFn: () => getTestResults(sha ?? ''),
    enabled: sha !== null,
    refetchInterval: 60_000,
  });
}

export interface TestSummary {
  readonly total: number;
  readonly ok: number;
  readonly notOk: number;
  readonly notTested: number;
  readonly untested: number;
}

export type ChangeStatus = TestMarkStatus | 'untested';

/**
 * Per change: not ok if any tester's latest mark is not ok, ok if anyone
 * tested it, not tested if every mark says it could not be tested (#3750),
 * untested without marks. One open finding is enough to flag a change.
 * staff-api counts the approval's findings the same way.
 */
export function changeStatus(noteId: string, marks: readonly TestMark[]): ChangeStatus {
  const own = marks.filter((m) => m.noteId === noteId);
  if (own.length === 0) return 'untested';
  if (own.some((m) => m.status === 'not_ok')) return 'not_ok';
  return own.every((m) => m.status === 'not_tested') ? 'not_tested' : 'ok';
}

export function summarize(notes: readonly ReleaseNote[], marks: readonly TestMark[]): TestSummary {
  const statuses = notes.map((n) => changeStatus(n.id, marks));
  return {
    total: notes.length,
    ok: statuses.filter((s) => s === 'ok').length,
    notOk: statuses.filter((s) => s === 'not_ok').length,
    notTested: statuses.filter((s) => s === 'not_tested').length,
    untested: statuses.filter((s) => s === 'untested').length,
  };
}

export interface OpenFinding {
  readonly noteId: string;
  readonly title: string;
  readonly by: string;
  readonly comment: string;
}

/** The not-ok marks still standing, with the change title in the reader's language. */
export function openFindings(
  notes: readonly ReleaseNote[],
  marks: readonly TestMark[],
  language: 'fi' | 'en'
): OpenFinding[] {
  const titles = new Map(notes.map((n) => [n.id, n[language].title]));
  return marks
    .filter((m) => m.status === 'not_ok' && titles.has(m.noteId))
    .map((m) => ({ noteId: m.noteId, title: titles.get(m.noteId) ?? '', by: m.by, comment: m.comment ?? '' }));
}

/** "First Last" from "First Last <email>". */
export function displayName(by: string): string {
  return by.replace(/\s*<[^>]*>\s*$/, '').trim() || by;
}

export interface UntestedChange {
  readonly noteId: string;
  readonly title: string;
}

/** Changes nobody has marked yet (#3526), in the reader's language. */
export function untestedChanges(
  notes: readonly ReleaseNote[],
  marks: readonly TestMark[],
  language: 'fi' | 'en'
): UntestedChange[] {
  return notes
    .filter((n) => changeStatus(n.id, marks) === 'untested')
    .map((n) => ({ noteId: n.id, title: n[language].title }));
}

export interface NotTestedChange {
  readonly noteId: string;
  readonly title: string;
  /** Who marked it, with their reason when they gave one. */
  readonly marks: readonly { readonly by: string; readonly reason: string | null }[];
}

/**
 * Changes testers marked as impossible to test here (#3750). The approval
 * dialog lists them with the reasons; they need no acknowledgement.
 */
export function notTestedChanges(
  notes: readonly ReleaseNote[],
  marks: readonly TestMark[],
  language: 'fi' | 'en'
): NotTestedChange[] {
  return notes
    .filter((n) => changeStatus(n.id, marks) === 'not_tested')
    .map((n) => ({
      noteId: n.id,
      title: n[language].title,
      marks: marks.filter((m) => m.noteId === n.id).map((m) => ({ by: m.by, reason: m.comment })),
    }));
}

export interface InheritedChange {
  readonly noteId: string;
  readonly title: string;
  /** Who marked it, and on which earlier version. */
  readonly marks: readonly { readonly by: string; readonly status: TestMarkStatus; readonly version: string | null }[];
}

/**
 * Changes whose standing marks all come from earlier versions (#3547): tested,
 * but not on the version being approved. The approval dialog lists them.
 */
export function inheritedOnlyChanges(
  notes: readonly ReleaseNote[],
  marks: readonly TestMark[],
  language: 'fi' | 'en'
): InheritedChange[] {
  return notes.flatMap((note) => {
    const own = marks.filter((m) => m.noteId === note.id);
    if (own.length === 0 || own.some((m) => !m.inherited)) return [];
    return [
      {
        noteId: note.id,
        title: note[language].title,
        marks: own.map((m) => ({ by: m.by, status: m.status, version: m.fromVersion ?? null })),
      },
    ];
  });
}
